import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import API, { UPLOADS_URL } from '../api/axios';

const ICONS = [
  'fas fa-star', 'fas fa-lightbulb', 'fas fa-graduation-cap', 
  'fas fa-globe', 'fas fa-users', 'fas fa-chart-line',
  'fas fa-heart', 'fas fa-shield-alt', 'fas fa-book-open'
];
const BG_COLORS = ['bg-blue-50', 'bg-green-50', 'bg-rose-50', 'bg-amber-50', 'bg-purple-50', 'bg-brand/10'];
const TEXT_COLORS = ['text-blue-500', 'text-green-500', 'text-rose-500', 'text-amber-500', 'text-purple-500', 'text-brand'];

const resolveFeatureImage = (img) => {
  if (!img) return null;
  if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('/')) {
    return img;
  }
  return `${UPLOADS_URL}/${img}`;
};

const StructuredPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const pageData = usePage(slug);
  const [sectionData, setSectionData] = useState({});

  useEffect(() => {
    API.get('/home-sections').then(res => {
      if (res.data?.data) {
        setSectionData(res.data.data[`structured_${slug}`] || {});
      }
    }).catch(console.error);
  }, [slug]);

  const heading = sectionData.heading || pageData.title || '';
  const subheading = sectionData.subheading || sectionData.sub_heading || '';
  const paragraphs = sectionData.paragraphs || '';
  const features = Array.isArray(sectionData.features) ? sectionData.features : [];
  const conclusion = sectionData.conclusion || '';

  return (
    <>
      <SEO 
        title={pageData.seo?.meta_title || pageData.title} 
        description={pageData.seo?.meta_description || heading} 
      />

      <PageBanner 
        title={pageData.banner_heading || pageData.title || ''} 
        subtitle={pageData.banner_sub_heading || ""} 
        breadcrumbs={[{ label: 'Home', link: '/' }, { label: pageData.title || "" }]} 
        backgroundImage={pageData.bannerImage} 
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/core-values.jpg`} 
        loading={pageData.loading} 
      />

      {/* Combined Section */}
      <section className="section bg-gray-50/50 relative overflow-hidden min-h-[60vh]">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl pointer-events-none" />
        
        <div className="container relative z-10">
          
          {(heading || subheading || paragraphs) && (
            <Reveal>
              <div className="text-center mb-16 max-w-4xl mx-auto">
                {subheading && <div className="kicker mx-auto">{subheading}</div>}
                {heading && (
                  <>
                    <h2 className="h-section text-3xl md:text-4xl font-bold mt-3">{heading}</h2>
                    <div className="divider-line mt-5 mb-8 mx-auto" />
                  </>
                )}
                
                
                
                {paragraphs && (
                  <div className="text-body text-[17px] leading-relaxed prose max-w-none mx-auto text-justify md:text-center" dangerouslySetInnerHTML={{ __html: paragraphs }} />
                )}
              </div>
            </Reveal>
          )}

          {features.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mt-10">
              {features.map((val, index) => {
                const icon = ICONS[index % ICONS.length];
                const bg = BG_COLORS[index % BG_COLORS.length];
                const color = TEXT_COLORS[index % TEXT_COLORS.length];
                const featureImg = resolveFeatureImage(val.image);

                return (
                  <Reveal key={index} delayMs={index * 100}>
                    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_35px_rgb(0,0,0,0.08)] group relative overflow-hidden flex flex-col">
                      {featureImg ? (
                        <div className="relative w-full h-52 overflow-hidden bg-gray-100">
                          <img 
                            src={featureImg} 
                            alt={val.title || 'Feature'} 
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      ) : (
                        <div className={`absolute top-0 right-0 w-32 h-32 ${bg} rounded-bl-full -z-0 opacity-50 transition-transform duration-500 group-hover:scale-110`} />
                      )}
                      
                      <div className="p-7 relative z-10 flex-1 flex flex-col">
                        <div className="flex items-center gap-3 mb-3">
                          {!featureImg && (
                            <div className={`w-12 h-12 shrink-0 ${bg} ${color} rounded-xl flex items-center justify-center text-xl shadow-sm`}>
                              <i className={icon} />
                            </div>
                          )}
                          <h3 className="text-[1.2rem] font-bold text-gray-900 leading-tight">{val.title}</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed text-[15px] flex-1">
                          {val.desc}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
          
          {conclusion && (
            <Reveal>
              <div className="mt-16 max-w-4xl mx-auto text-center">
                <div className="text-body text-[17px] leading-relaxed prose max-w-none mx-auto text-justify md:text-center" dangerouslySetInnerHTML={{ __html: conclusion }} />
              </div>
            </Reveal>
          )}

        </div>
      </section>

    </>
  );
};

export default StructuredPage;
